/* eslint-disable operator-linebreak */
/* eslint-disable arrow-parens */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parseLocale } from '@tesla/parse-locale';

// actions
import {
  setGoogleMap,
  setGoogleMapGeolocation,
  setGoogleMapEntryPoint,
  restoreTripFromURL,
} from '../../reducers/GoogleMap/actions';
import { fetchSuperchargers } from '../../reducers/Superchargers/actions';
import { setPlace } from '../../reducers/Places/actions';
import { parseAppTTP } from '../../reducers/App/actions';

// components
import wrapper from './wrapper';
import GoogleMapView from '../../components/GoogleMap';

// helpers
import MapUtils from '../../lib/MapUtils';
import MapMarkers from '../../lib/MapMarkers';
import MapWindow from '../../lib/MapWindow';
import {
  getJsonFromUrl,
  countryWorkarounds,
  normalizeMapLanguage,
  constructTripData,
  getTripFromURL,
} from '../../lib/Utils';
import PlaceUtils from '../../lib/PlaceUtils';

const { App: { locale = 'en_US' } = {} } = window.tesla || {};
const { country: region, lang: language } = parseLocale(locale);

const GoogleMapPropTypes = {
  loaded: PropTypes.bool.isRequired, // Lets the application know the Google API & Map has loaded
  google: PropTypes.shape({
    // Google Map API passed as props from higher order component
    maps: PropTypes.object,
  }),
  dispatchSetGoogleMap: PropTypes.func.isRequired, // Lets the application know that the map is ready
  dispatchFetchSuperchargers: PropTypes.func.isRequired, // Gets superchargers from API service
  zoom: PropTypes.number.isRequired, // Default zoom level for google map
  isMobile: PropTypes.bool.isRequired, // True when the app is loaded on a handheld sized device
  dispatchSetGoogleMapGeolocation: PropTypes.func.isRequired, // If geolocation was used
  fieldsLength: PropTypes.number.isRequired, // Number of places/fields in a users trip
  dispatchSetPlace: PropTypes.func.isRequired, // Handles setting origin, stopover, or destination
  dispatchSetGoogleMapEntryPoint: PropTypes.func.isRequired, // Lets app know if user used entry point
  dispatchRestoreTripFromURL: PropTypes.func.isRequired, // routes trip from url params
  superchargers: PropTypes.arrayOf(PropTypes.object).isRequired, // Network of superchargers for the google map
};

const GoogleMapDefaultProps = {
  google: null,
};

class GoogleMap extends Component {
  constructor(props) {
    super(props);

    this.loadMap = this.loadMap.bind(this);
    this.initMap = this.initMap.bind(this);
    this.getGeolocationFromBrowser = this.getGeolocationFromBrowser.bind(this);

    this.node = {};
    this.lat = 37.776443;
    this.lng = -122.451978;
  }

  componentDidUpdate(prevProps) {
    const { superchargers } = this.props;
    const entryPoint = getJsonFromUrl();

    if (
      !entryPoint.destination &&
      superchargers.length > 0 &&
      superchargers !== prevProps.superchargers
    ) {
      this.resumeTrip();
    }
  }

  getGeolocationFromBrowser() {
    const url = window.location.href;
    const queryParams = url.indexOf('?t=');
    const { dispatchSetGoogleMapGeolocation } = this.props;

    if (queryParams >= 0) return;

    const entryPoint = getJsonFromUrl();

    if (navigator.geolocation && !entryPoint.destination) {
      // eslint-disable-next-line arrow-parens
      navigator.geolocation.getCurrentPosition(position => {
        this.lat = position.coords.latitude;
        this.lng = position.coords.longitude;

        MapUtils.centerMap(this.lat, this.lng);

        dispatchSetGoogleMapGeolocation(true);
      });
    }

    if (entryPoint.destination || entryPoint.origin) {
      this.prePopulateField(entryPoint);
    }
  }

  resumeTrip() {
    const entryPoint = getJsonFromUrl();
    const queryParams = window.location.href.indexOf('v=');
    // eslint-disable-next-line object-curly-newline
    const { vehicle, origin, stops, destination } = getTripFromURL();
    const { dispatchRestoreTripFromURL } = this.props;

    if (!entryPoint.destination && queryParams >= 0) {
      // construct trip object for dispatching
      const tripData = constructTripData({
        vehicle,
        origin,
        stops,
        destination,
      });

      dispatchRestoreTripFromURL(tripData);
    }
  }

  geoCodeCountry() {
    const { google } = window;

    MapUtils.initGeocoder();
    this.geocoder = MapUtils.getGeocoder();

    this.geocoder.geocode({ address: region }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results[0]) {
          this.lat = results[0].geometry.location.lat();
          this.lng = results[0].geometry.location.lng();
        }
      } else if (region === 'CA') {
        // Google isn't returning a location for en_CA.
        // When this happens, set the lat/lng for vancouver.
        this.lat = 49.246292;
        this.lng = -123.116226;
      }
      this.initMap();
    });
  }

  loadMap(node) {
    const { google } = this.props;

    if (this.props && google) {
      this.node = node;
      this.geoCodeCountry();
    }
  }

  initMap() {
    const {
      dispatchFetchSuperchargers,
      dispatchSetGoogleMap,
      google,
      zoom,
      isMobile,
    } = this.props;

    // instantiate map
    const mapConfig = Object.assign(
      {},
      {
        center: new google.maps.LatLng(this.lat, this.lng),
        minZoom: 3,
        zoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        draggableCursor: 'default',
        disableDefaultUI: true,
        zoomControl: !isMobile,
      },
    );

    window.map = new google.maps.Map(this.node, mapConfig);

    // let the application know the map has been instantiated
    dispatchSetGoogleMap(true);

    // fetch supercharger network
    dispatchFetchSuperchargers();

    // allow for custom marker windows
    MapWindow.initOverlayView();

    // prevent users from scrolling off the edge of the map
    MapUtils.initGravity();

    // attempt to get users geolocation from browser
    this.getGeolocationFromBrowser();
  }

  prePopulateField(entryPoint) {
    const {
      dispatchSetGoogleMapEntryPoint,
      dispatchSetPlace,
      google,
      fieldsLength,
    } = this.props;

    if (entryPoint.destination) {
      PlaceUtils.focusNextField(0);
    } else {
      PlaceUtils.focusNextField(-1);
    }

    this.geocoder.geocode(
      { address: entryPoint.destination || entryPoint.origin },
      (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            const addressDetails = results[0];
            const place = document.activeElement;
            place.value = addressDetails.formatted_address;

            dispatchSetPlace(place, addressDetails, fieldsLength);
            dispatchSetGoogleMapEntryPoint(true);

            const field = {
              id: place.name,
              lat: addressDetails.geometry.location.lat(),
              lng: addressDetails.geometry.location.lng(),
              order: entryPoint.destination ? 1 : 0,
            };
            MapMarkers.updateMapMarker(field, fieldsLength);

            if (entryPoint.destination) {
              PlaceUtils.focusNextField(-1);
            } else {
              PlaceUtils.focusNextField(0);
            }
          }
        }
      },
    );
  }

  render() {
    return (
      <div>
        <GoogleMapView loadMap={this.loadMap} {...this.props} />
      </div>
    );
  }
}

GoogleMap.propTypes = GoogleMapPropTypes;

GoogleMap.defaultProps = GoogleMapDefaultProps;

const mapStateToProps = state => ({
  zoom: state.GoogleMap.zoom,
  isMobile: state.App.isMobile,
  fieldsLength: state.Places.fieldsLength,
  editing: state.Places.editing,
  directionsLoaded: state.Directions.directionsLoaded,
  showAltMap: state.Directions.showAltMap,
  outOfRangeLabel: state.RouteSummary.outOfRangeLabel,
  superchargers: state.Superchargers.superchargers,
});

const mapDispatchToProps = dispatch => ({
  dispatchSetGoogleMap: mapIsReady => {
    dispatch(setGoogleMap(mapIsReady));
  },
  dispatchFetchSuperchargers: () => {
    dispatch(fetchSuperchargers());
  },
  dispatchSetPlace: (field, place, fieldsLen) => {
    dispatch(setPlace(field, place, fieldsLen));
  },
  dispatchSetGoogleMapGeolocation: geolocation => {
    dispatch(setGoogleMapGeolocation(geolocation));
  },
  dispatchSetGoogleMapEntryPoint: entryPoint => {
    dispatch(setGoogleMapEntryPoint(entryPoint));
  },
  dispatchRestoreTripFromURL: trip => {
    dispatch(restoreTripFromURL(trip));
  },
  dispatchParseAEM: initialState => {
    dispatch(parseAppTTP(initialState));
  },
});

const GoogleMapContainer = wrapper({
  client: 'gme-teslamotorsinc',
  channel: 'trip_planner',
  version: '3.34', // We MUST use version 3.31 until there is better event handling with the new renderer animations
  libraries: ['places'],
  language: normalizeMapLanguage(language, region),
  region: countryWorkarounds(region),
})(GoogleMap);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(GoogleMapContainer);
